import { useState } from 'react';

import type { ListHomeCareVendors } from '@virtual-care/client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Label,
  Textarea,
} from '@virtual-care/shadcn';
import { useAction } from '@virtual-care/ui';

export type HomeCareVendor = ListHomeCareVendors['records'][number];

interface RejectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vendorId: string;
  vendorName: string;
}

export function RejectionDialog({
  open,
  onOpenChange,
  vendorId,
  vendorName,
}: RejectionDialogProps) {
  const [rejectionReason, setRejectionReason] = useState('');

  const rejectMutation = useAction('POST /homecare/{id}/reject', {
    invalidate: ['GET /homecare'],
    onSuccess: () => {
      onOpenChange(false);
      setRejectionReason('');
    },
  });

  const handleConfirm = () => {
    if (rejectionReason.length >= 10) {
      rejectMutation.mutate({ id: vendorId, reason: rejectionReason });
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="rtl:text-right">
            Reject Vendor
          </AlertDialogTitle>
          <AlertDialogDescription className="rtl:text-right">
            Please provide a reason for rejecting {vendorName}. This reason will
            be sent to the vendor.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="reason">Rejection Reason</Label>
            <Textarea
              id="reason"
              placeholder="Enter the reason for rejection (minimum 10 characters)"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
            />
            {rejectionReason.length > 0 && rejectionReason.length < 10 && (
              <p className="text-sm text-red-500">
                Reason must be at least 10 characters
              </p>
            )}
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={rejectionReason.length < 10 || rejectMutation.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {rejectMutation.isPending ? 'Rejecting...' : 'Reject Vendor'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
